/**
 * Marketing Card — glass / glass-strong / flat with optional glow + interactive hover.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-010
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

type CardVariant = 'glass' | 'glass-strong' | 'flat';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  glow?: boolean;
  interactive?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASSES: Record<CardVariant, string> = {
  glass: 'glass-card',
  'glass-strong': 'glass-strong rounded-2xl',
  flat: 'bg-ink-900 border border-white/5 rounded-2xl',
};

export function Card({
  variant = 'glass',
  glow = false,
  interactive = false,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={cn(
        'p-6 lg:p-8',
        VARIANT_CLASSES[variant],
        glow && 'shadow-glow-cyan-sm',
        interactive &&
          'transition-all duration-300 hover:-translate-y-1 hover:border-cyan-500/30 hover:shadow-glow-cyan-md',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
